import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useWebgate } from "@xvia/webgate-connect";
import classNames from "classnames";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { BalancesDropdown } from "./BalancesDropdown";
import { FloatingHeader } from "./floating-header";
import "./header.css";
import { NotaPiauienseService } from "./service/NotaPiauiense";
import { ApiResponse } from "./service/models";
import { formatCurrency } from "./utils/formatCurrency";

export interface HeaderProps {
  showFloatingHeader: boolean;
  SearchComponent: FunctionComponent<any>;
  LoginComponent: FunctionComponent<any>;
}
export const Header = ({
  SearchComponent,
  showFloatingHeader,
  LoginComponent,
}: HeaderProps) => {
  const mainClassName = classNames({
    "xvia-catalog-header": true,
    "xvia-catalog-header--fixed": showFloatingHeader,
    "xvia-catalog-header--visible": !showFloatingHeader,
  });
  const [isBalancesDropdownOpen, setIsBalancesDropdownOpen] = useState(false);
  const balanceRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        balanceRef.current &&
        !balanceRef.current.contains(event.target as Node)
      ) {
        setIsBalancesDropdownOpen(false);
      }
    };

    if (isBalancesDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isBalancesDropdownOpen]);

  const handleCloseBalancesDropdown = () => {
    setIsBalancesDropdownOpen(false);
  };

  const handleBalanceClick = (e: React.MouseEvent) => {
    setIsBalancesDropdownOpen(!isBalancesDropdownOpen);
  };

  const handleBalanceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsBalancesDropdownOpen(!isBalancesDropdownOpen);
    }
  };

  const [notaPiauiense, setNotaPiauiense] = useState<ApiResponse | null>(null);

  const { accessToken } = useWebgate();

  useEffect(() => {
    if (accessToken) {
      NotaPiauienseService.listNotas(accessToken).then((response) => {
        setNotaPiauiense(response);
      });
    }
  }, [accessToken]);

  return (
    <div className={mainClassName}>
      <div className="xvia-catalog-header__container">
        <FloatingHeader RightComponent={SearchComponent} />

        <div className="xvia-catalog-header__left-side">
          <div className="xvia-catalog-header__image-wrapper">
            <img
              height={48}
              src={`${process.env.PUBLIC_URL}/images/svgs/logo_white.svg`}
              className="xvia-catalog-header__image"
              alt="Governo do Estado do Piauí"
            />
          </div>

          <div
            className="balance-container"
            style={{ position: "relative" }}
            ref={balanceRef}
          >
            {accessToken && (
              <div
                role="button"
                tabIndex={0}
                className="balance"
                onClick={handleBalanceClick}
                onKeyDown={handleBalanceKeyDown}
                aria-label="Abrir dropdown de saldo da Nota Piauiense"
              >
                <span className="balance__label">Nota Piauiense</span>
                <span className="balance__value">
                  {formatCurrency(
                    notaPiauiense?.data?.saldoNotaPovo?.saldoLiberado
                  )}
                </span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  color="#fff"
                  className={classNames("balance__icon", {
                    "balance__icon--rotated": isBalancesDropdownOpen,
                  })}
                />
              </div>
            )}

            <BalancesDropdown
              isOpen={isBalancesDropdownOpen}
              onClose={handleCloseBalancesDropdown}
              notaPiauiense={notaPiauiense}
            />
          </div>
        </div>

        <div className="right-side">
          <div className="xvia-catalog-header__links">
            <ul>
              <li>
                <a href="/app/catalog/list/servicos-ao-cidadao">Cidadão</a>
              </li>
              <li>
                <a href="/app/catalog/list/servidor-publico">Servidor</a>
              </li>
              <li>
                <a href="/app/catalog/list/empresas">Empresa</a>
              </li>
            </ul>
          </div>
          <div className="xvia-catalog-header__elements">
            <LoginComponent />
          </div>
        </div>
      </div>
    </div>
  );
};
